(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal(root) {
    const items = root.querySelectorAll('.tt-reveal:not([data-tt-ready])');
    items.forEach((item) => item.setAttribute('data-tt-ready', 'true'));
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('tt-in'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('tt-in');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    items.forEach((item) => observer.observe(item));
  }

  function initFeatures(root) {
    root.querySelectorAll('[data-tt-features]:not([data-tt-ready])').forEach((section) => {
      section.setAttribute('data-tt-ready', 'true');
      const items = Array.from(section.querySelectorAll('[data-tt-feature-item]'));
      const images = Array.from(section.querySelectorAll('[data-tt-feature-image]'));
      const current = section.querySelector('[data-tt-feature-current]');
      const activate = (index) => {
        items.forEach((item, itemIndex) => {
          const active = itemIndex === index;
          item.classList.toggle('tt-active', active);
          item.setAttribute('aria-expanded', String(active));
        });
        images.forEach((image, imageIndex) => image.classList.toggle('tt-on', imageIndex === index));
        if (current) current.textContent = String(index + 1).padStart(2, '0');
      };
      items.forEach((item, index) => {
        item.addEventListener('click', () => activate(index));
        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          activate(index);
        });
        item.addEventListener('mouseenter', () => activate(index));
      });
    });
  }

  function initPress(root) {
    root.querySelectorAll('[data-tt-press]:not([data-tt-ready])').forEach((section) => {
      section.setAttribute('data-tt-ready', 'true');
      const quotes = Array.from(section.querySelectorAll('[data-tt-press-quote]'));
      const logos = Array.from(section.querySelectorAll('[data-tt-press-logo]'));
      const dots = Array.from(section.querySelectorAll('[data-tt-press-dot]'));
      const mobileLogo = section.querySelector('[data-tt-press-mobile-logo]');
      const autoplay = section.dataset.ttPressAutoplay === 'true' && !reduceMotion && quotes.length > 1;
      const interval = Math.max(3000, Number(section.dataset.ttPressInterval) || 5000);
      let activeIndex = 0;
      let timer;
      const activate = (index) => {
        activeIndex = index;
        quotes.forEach((quote, itemIndex) => quote.classList.toggle('is-active', itemIndex === index));
        logos.forEach((logo, itemIndex) => {
          const active = itemIndex === index;
          logo.classList.toggle('is-active', active);
          logo.setAttribute('aria-pressed', String(active));
        });
        dots.forEach((dot, itemIndex) => dot.classList.toggle('is-active', itemIndex === index));
        if (mobileLogo && logos[index]) mobileLogo.innerHTML = logos[index].innerHTML;
      };
      const stop = () => {
        if (timer) window.clearTimeout(timer);
        timer = undefined;
      };
      const schedule = () => {
        stop();
        if (!autoplay || !section.isConnected) return;
        timer = window.setTimeout(() => {
          activate((activeIndex + 1) % quotes.length);
          schedule();
        }, interval);
      };
      logos.forEach((logo, index) => {
        logo.addEventListener('mouseenter', () => activate(index));
        logo.addEventListener('focus', () => activate(index));
        logo.addEventListener('click', () => {
          activate(index);
          schedule();
        });
      });
      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        activate(index);
        schedule();
      }));
      section.addEventListener('mouseenter', stop);
      section.addEventListener('mouseleave', schedule);
      section.addEventListener('focusin', stop);
      section.addEventListener('focusout', (event) => {
        if (!section.contains(event.relatedTarget)) schedule();
      });
      activate(0);
      schedule();
    });
  }

  function initMatch(root) {
    root.querySelectorAll('[data-tt-match]:not([data-tt-ready])').forEach((section) => {
      section.setAttribute('data-tt-ready', 'true');
      const strip = section.querySelector('[data-tt-match-strip]');
      const cards = Array.from(section.querySelectorAll('.tt-card'));
      const current = section.querySelector('[data-tt-match-current]');
      const progress = section.querySelector('[data-tt-match-progress]');
      const previous = section.querySelector('[data-tt-match-prev]');
      const next = section.querySelector('[data-tt-match-next]');
      if (!strip || !cards.length) return;
      const update = () => {
        const max = strip.scrollWidth - strip.clientWidth;
        const ratio = max > 0 ? strip.scrollLeft / max : 0;
        if (progress) progress.style.width = `${Math.max(6, ratio * 100)}%`;
        const center = strip.scrollLeft + strip.clientWidth / 2;
        let nearest = 0;
        let distance = Infinity;
        cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          if (Math.abs(center - cardCenter) < distance) {
            distance = Math.abs(center - cardCenter);
            nearest = index;
          }
        });
        if (current) current.textContent = String(nearest + 1).padStart(2, '0');
        if (previous) previous.disabled = strip.scrollLeft <= 2;
        if (next) next.disabled = strip.scrollLeft >= max - 2;
      };
      const step = () => cards[0].offsetWidth + (parseFloat(getComputedStyle(strip).gap) || 24);
      if (previous) previous.addEventListener('click', () => strip.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      if (next) next.addEventListener('click', () => strip.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      let dragStart = 0;
      let scrollStart = 0;
      let dragged = false;
      let activePointer = null;
      strip.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        dragStart = event.clientX;
        scrollStart = strip.scrollLeft;
        dragged = false;
        activePointer = event.pointerId;
      });
      strip.addEventListener('pointermove', (event) => {
        if (activePointer !== event.pointerId) return;
        const distance = event.clientX - dragStart;
        if (!dragged && Math.abs(distance) > 5) {
          dragged = true;
          strip.classList.add('tt-dragging');
          strip.setPointerCapture(event.pointerId);
        }
        if (!dragged) return;
        strip.scrollLeft = scrollStart - distance;
      });
      const releaseDrag = (event) => {
        if (activePointer !== event.pointerId) return;
        strip.classList.remove('tt-dragging');
        if (strip.hasPointerCapture(event.pointerId)) strip.releasePointerCapture(event.pointerId);
        activePointer = null;
      };
      strip.addEventListener('pointerup', releaseDrag);
      strip.addEventListener('pointercancel', releaseDrag);
      strip.addEventListener('click', (event) => {
        if (!dragged) return;
        event.preventDefault();
        dragged = false;
      }, true);
      strip.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  function initCommunity(root) {
    root.querySelectorAll('[data-tt-community]:not([data-tt-ready])').forEach((section) => {
      section.setAttribute('data-tt-ready', 'true');
      const modal = section.querySelector('[data-tt-video-modal]');
      const player = modal && modal.querySelector('.tt-video-player');
      const close = () => {
        if (!modal || !player) return;
        modal.classList.remove('tt-open');
        modal.setAttribute('aria-hidden', 'true');
        player.pause();
        player.removeAttribute('src');
        document.body.classList.remove('tt-modal-open');
      };
      section.querySelectorAll('[data-tt-video]').forEach((tile) => tile.addEventListener('click', () => {
        const source = tile.getAttribute('data-tt-video');
        if (!source || !modal || !player) return;
        player.src = source;
        modal.classList.add('tt-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('tt-modal-open');
        player.play().catch(() => {});
      }));
      section.querySelectorAll('[data-tt-video-close]').forEach((button) => button.addEventListener('click', close));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal && modal.classList.contains('tt-open')) close();
      });
    });
  }

  function init(root = document) {
    initReveal(root);
    initFeatures(root);
    initPress(root);
    initMatch(root);
    initCommunity(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
